import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { pricingPlans } from '../mock';
import { captureEvent } from '../utils/posthog';
import { EVENTS, PROPERTIES } from '../utils/analytics';

export const Pricing = () => {
  const planViewTrackedRef = useRef(new Set());

  const scrollToWaitlist = (planName, e) => {
    // Prevent any default button behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Use a small delay to ensure DOM is ready, or try multiple times
    const attemptScroll = (attempts = 0) => {
      const element = document.getElementById('waitlist');
      if (element) {
        // Restore scrollIntoView if it was overridden
        if (typeof element.scrollIntoView !== 'function' || element.scrollIntoView.toString().includes('() => {}')) {
          // Restore the native scrollIntoView method
          delete element.scrollIntoView;
        }
        
        // Scroll to waitlist section smoothly
        try {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Track the button click
          captureEvent(EVENTS.CTA_BUTTON_CLICKED, {
            [PROPERTIES.CTA_LOCATION]: 'pricing',
            [PROPERTIES.CTA_TEXT]: 'Book Early Access',
            [PROPERTIES.NAVIGATION_TARGET]: 'waitlist',
            [PROPERTIES.PRICING_PLAN_NAME]: planName,
          });
        } catch (error) {
          console.warn('Scroll failed, trying alternative method:', error);
          // Fallback: use window.scrollTo
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          window.scrollTo({
            top: rect.top + scrollTop - 100, // 100px offset from top
            behavior: 'smooth'
          });
        }
      } else if (attempts < 3) {
        // Retry if element not found (might be still rendering)
        setTimeout(() => attemptScroll(attempts + 1), 100);
      } else {
        console.warn('Waitlist section not found after multiple attempts');
      }
    };
    
    attemptScroll();
  };
  // Track pricing plan views
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const planName = entry.target.getAttribute('data-plan-name');
            if (planName && !planViewTrackedRef.current.has(planName)) {
              planViewTrackedRef.current.add(planName);
              captureEvent(EVENTS.PRICING_PLAN_VIEWED, {
                [PROPERTIES.PRICING_PLAN_NAME]: planName,
              });
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // Observe all pricing cards
    const cards = document.querySelectorAll('[data-plan-name]');
    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  return (
    <section id="pricing" className="py-24 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>

      <div className="container mx-auto px-4">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, <span className="text-gradient">transparent pricing</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Start free, upgrade when you're ready to scale. No hidden fees, cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              data-plan-name={plan.name}
              className={`pricing-card relative glass rounded-3xl p-8 animate-fade-in-up stagger-${index + 1} ${
                plan.highlighted
                  ? 'border-2 border-yellow-500 shadow-2xl lg:scale-105'
                  : 'border border-gray-800'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-500 text-black font-bold px-4 py-1 rounded-full text-sm">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                <div className="mb-2">
                  {plan.originalPrice && (
                    <div className="mb-1">
                      <span className="text-2xl text-gray-500 line-through">{plan.originalPrice}</span>
                    </div>
                  )}
                  <span className="text-5xl font-bold text-white">{plan.price}</span>
                  {plan.originalPrice && (
                    <span className="ml-3 inline-block bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
                      SAVE 50%
                    </span>
                  )}
                </div>
                <p className="text-gray-400">{plan.period}</p>
              </div>

              <div className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={(e) => scrollToWaitlist(plan.name, e)}
                type="button"
                className={`w-full font-semibold py-6 text-lg transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-black btn-primary'
                    : 'bg-white/5 hover:bg-white/10 text-white border border-gray-700'
                }`}
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* Early Access Discount Note */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-8 text-center border border-yellow-500/30">
            <div className="flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-yellow-500">Early Access Special Pricing</h3>
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              The discounted prices shown above apply <span className="text-yellow-500 font-semibold">exclusively to users who register during the early access period</span>. Lock in these special rates now and enjoy earlyaccess discounts for the first 6 months of your subscription!
            </p>
            <div className="mt-4 inline-flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Limited time offer • Early access pricing secured for lifetime.
            </div>
          </div>
        </div>

        {/* Trust section */}
        <div className="mt-20 text-center">
          <p className="text-gray-400 mb-4">Trusted by 10,000+ sellers across India</p>
          <div className="flex flex-wrap justify-center gap-8 items-center opacity-50">
            <div className="text-gray-600 font-bold text-xl">AMAZON</div>
            <div className="text-gray-600 font-bold text-xl">FLIPKART</div>
            <div className="text-gray-600 font-bold text-xl">SHOPIFY</div>
          </div>
        </div>
      </div>
    </section>
  );
};